import { menuErrorFragment } from "@saleor/fragments/errors";
import {
  menuItemFragment,
  menuItemNestedFragment
} from "@saleor/fragments/navigation";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import {
  MenuBulkDelete,
  MenuBulkDeleteVariables
} from "./types/MenuBulkDelete";
import { MenuCreate, MenuCreateVariables } from "./types/MenuCreate";
import { MenuDelete, MenuDeleteVariables } from "./types/MenuDelete";
import {
  MenuItemCreate,
  MenuItemCreateVariables
} from "./types/MenuItemCreate";
import {
  MenuItemUpdate,
  MenuItemUpdateVariables
} from "./types/MenuItemUpdate";
import { MenuToFooter, MenuToFooterVariables } from "./types/MenuToFooter";
import { MenuToMain, MenuToMainVariables } from "./types/MenuToMain";
import { MenuToNewMain, MenuToNewMainVariables } from "./types/MenuToNewMain";
import {
  MenuToSecondary,
  MenuToSecondaryVariables
} from "./types/MenuToSecondary";
import { MenuUpdate, MenuUpdateVariables } from "./types/MenuUpdate";
import {
  PageFooterCreate,
  PageFooterCreateVariables
} from "./types/PageFooterCreate";
import {
  PageFooterUpdate,
  PageFooterUpdateVariables
} from "./types/PageFooterUpdate";

const menuCreate = gql`
  ${menuErrorFragment}
  mutation MenuCreate($input: MenuCreateInput!) {
    menuCreate(input: $input) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
      menu {
        id
      }
    }
  }
`;
export const MenuCreateMutation = TypedMutation<
  MenuCreate,
  MenuCreateVariables
>(menuCreate);

const menuBulkDelete = gql`
  ${menuErrorFragment}
  mutation MenuBulkDelete($ids: [ID]!) {
    menuBulkDelete(ids: $ids) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }
  }
`;
export const MenuBulkDeleteMutation = TypedMutation<
  MenuBulkDelete,
  MenuBulkDeleteVariables
>(menuBulkDelete);

const menuDelete = gql`
  ${menuErrorFragment}
  mutation MenuDelete($id: ID!) {
    menuDelete(id: $id) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }
  }
`;
export const MenuDeleteMutation = TypedMutation<
  MenuDelete,
  MenuDeleteVariables
>(menuDelete);

const menuToMain = gql`
  ${menuErrorFragment}
  mutation MenuToMain($menuId: ID!) {
    assignNavigation(menu: $menuId, navigationType: MAIN) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }
  }
`;
export const MenuToMainMutation = TypedMutation<
  MenuToMain,
  MenuToMainVariables
>(menuToMain);

const menuToNewMain = gql`
  ${menuErrorFragment}
  mutation MenuToNewMain($menuId: ID!) {
    assignNavigation(menu: $menuId, navigationType: NEWMAIN) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }
  }
`;
export const MenuToNewMainMutation = TypedMutation<
  MenuToNewMain,
  MenuToNewMainVariables
>(menuToNewMain);

const menuToSecondary = gql`
  ${menuErrorFragment}
  mutation MenuToSecondary($menuId: ID!) {
    assignNavigation(menu: $menuId, navigationType: SECONDARY) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }
  }
`;
export const MenuToSecondaryMutation = TypedMutation<
  MenuToSecondary,
  MenuToSecondaryVariables
>(menuToSecondary);

const menuToFooter = gql`
  ${menuErrorFragment}
  mutation MenuToFooter($menuId: ID!) {
    assignNavigation(menu: $menuId, navigationType: FOOTER) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }
  }
`;
export const MenuToFooterMutation = TypedMutation<
  MenuToFooter,
  MenuToFooterVariables
>(menuToFooter);

const menuItemCreate = gql`
  ${menuErrorFragment}
  ${menuItemNestedFragment}
  mutation MenuItemCreate($input: MenuItemCreateInput!) {
    menuItemCreate(input: $input) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
      menuItem {
        menu {
          id
          items {
            ...MenuItemNestedFragment
          }
        }
      }
    }
  }
`;
export const MenuItemCreateMutation = TypedMutation<
  MenuItemCreate,
  MenuItemCreateVariables
>(menuItemCreate);

const menuUpdate = gql`
  ${menuErrorFragment}
  mutation MenuUpdate(
    $id: ID!
    $name: String!
    $moves: [MenuItemMoveInput]!
    $removeIds: [ID]!
  ) {
    menuUpdate(id: $id, input: { name: $name }) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }

    menuItemMove(menu: $id, moves: $moves) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }

    menuItemBulkDelete(ids: $removeIds) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
    }
  }
`;
export const MenuUpdateMutation = TypedMutation<
  MenuUpdate,
  MenuUpdateVariables
>(menuUpdate);

const menuItemUpdate = gql`
  ${menuErrorFragment}
  ${menuItemFragment}
  mutation MenuItemUpdate($id: ID!, $input: MenuItemInput!) {
    menuItemUpdate(id: $id, input: $input) {
      errors: menuErrors {
        ...MenuErrorFragment
      }
      menuItem {
        ...MenuItemFragment
      }
    }
  }
`;
export const MenuItemUpdateMutation = TypedMutation<
  MenuItemUpdate,
  MenuItemUpdateVariables
>(menuItemUpdate);

const pageFooterCreate = gql`
  mutation PageFooterCreate($input: PageFooterCreateInput!) {
    pageFooterCreate(input: $input) {
      pageFooter {
        id
        customerService
        wholesaleInquiries
        mediaPlatformText
        paymentMethodText
      }
      errors {
        message
      }
    }
  }
`;
export const PageFooterCreateMutation = makeMutation<
  PageFooterCreate,
  PageFooterCreateVariables
>(pageFooterCreate);

const pageFooterUpdate = gql`
  mutation PageFooterUpdate($id: ID!, $input: PageFooterCreateInput!) {
    pageFooterUpdate(id: $id, input: $input) {
      pageFooter {
        id
        customerService
        wholesaleInquiries
        mediaPlatformText
        paymentMethodText
      }
      errors {
        message
      }
    }
  }
`;

export const PageFooterUpdateMutation = makeMutation<
  PageFooterUpdate,
  PageFooterUpdateVariables
>(pageFooterUpdate);
